import { useEffect, useState } from 'react'

import axios from 'axios'

import GetSiteUrls from '@/components/global/SiteUrls'

export interface UserStatusData {
  isLoggedIn: boolean
  isLoading: boolean
  hasStartedFetching: boolean
}

declare global {
  interface Window {
    userStatus?: UserStatusData
  }
}

const getUserStatus = async (): Promise<{ isLoggedIn: boolean }> => {
  const apiUrl = GetSiteUrls().ConfigSiteApiUrl
  const protocol = apiUrl.includes('local') ? 'http' : 'https'
  const url = `${protocol}://${apiUrl}/v3.0/user`
  try {
    const resp = await axios.get<{ id: string }>(url, { withCredentials: true })
    const isLoggedIn = resp.status === 200 && resp.data.id !== ''
    return { isLoggedIn }
  } catch (error) {
    return { isLoggedIn: false }
  }
}

/**
 * This hook is used to get the user status from the window object if available.
 *
 * It is used to avoid calling the API route multiple times.
 * Since NavBar is re-rendered multiple times,
 * we need to store the user status in the window object.
 *
 * @returns {Object} { isLoggedIn: boolean, isLoading: boolean }
 */
function useGetUserStatus() {
  const [data, setData] = useState<UserStatusData>(
    // to support SSR/SSG, we need to check if the window object is available
    typeof window !== 'undefined' && window.userStatus
      ? window.userStatus
      : { isLoggedIn: false, isLoading: true, hasStartedFetching: false }
  )

  const setUserStatus = (userStatus: Partial<UserStatusData>) => {
    setData((prev) => {
      if (typeof window !== 'undefined') {
        window.userStatus = { ...prev, ...userStatus }
      }
      return { ...prev, ...userStatus }
    })
  }

  useEffect(() => {
    const fetchUserStatus = async () => {
      if (window.userStatus?.hasStartedFetching) return
      setUserStatus({ isLoading: true, hasStartedFetching: true })
      const { isLoggedIn } = await getUserStatus()
      setUserStatus({ isLoggedIn, isLoading: false })
    }
    fetchUserStatus()
  }, [])

  return data
}

export default useGetUserStatus
